<script setup>
import { computed, ref } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useCompetitorAppConfig } from 'src/composables/useCompetitorAppConfig';
import { useChartDprResize } from 'src/composables/useChartDprResize';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, Title);

const props = defineProps({
  applicationType: {
    type: String,
    required: true,
  },
  values: {
    type: Object,
    required: true,
  },
  color: {
    type: String,
    required: false,
    default: '#7a7a7a',
  },
});

const chartRef = ref(null);

useChartDprResize(chartRef);

const { getApplicationType, getTechnicalPropertyType } = useCompetitorAppConfig();

const applicationTypeConfig = computed(() => getApplicationType(props.applicationType));

const angleSortFunction = (a, b) => {
  const varA = parseInt(a.code);
  const varB = parseInt(b.code);

  if (varA > varB) {
    return 1;
  }

  if (varA < varB) {
    return -1;
  }

  return 0;
};

const sortedChromaValues = computed(() => [...props.values.chroma].sort(angleSortFunction));
const sortedHueValues = computed(() => [...props.values.hue].sort(angleSortFunction));

const chromaValues = computed(() => sortedChromaValues.value.map((row) => row.value));
const hueValues = computed(() => sortedHueValues.value.map((row) => row.value));
const angleNames = sortedHueValues.value.map((row) => row.code);

const chromaConfig = getTechnicalPropertyType('chroma');
const hueConfig = getTechnicalPropertyType('hue');

const data = computed(() => ({
  datasets: [
    {
      label: `${chromaConfig?.title} / ${hueConfig?.title}`,
      data: hueValues.value.map((hueValue, i) => ({
        // Manipulate x (hue) values for a more logical chart, see https://trello.com/c/F6QPUFdr
        x: (() => {
          if ((hueValue * 2 + 360) / 360 > 2) {
            return hueValue - 360;
          }

          return hueValue;
        })(),
        y: chromaValues.value[i]
      })),
      backgroundColor: props.color,
      borderColor: props.color,
      tension: 0.4
    }
  ]
}));

const chartOptions = computed(() => {
  let maxX = Math.max(...data.value.datasets.map((ds) => ds.data[0].x));
  let maxY = Math.max(...data.value.datasets.map((ds) => ds.data[0].y));
  let minX = Math.min(...data.value.datasets.map((ds) => ds.data[0].x));
  let minY = Math.min(...data.value.datasets.map((ds) => ds.data[0].y));

  // Add a buffer of 10, rounded up to the next multiple of 5 so e.g. maxX value of 82 will become 95
  maxX = Math.ceil((maxX + 10) / 5) * 5;
  maxY = Math.ceil((maxY + 10) / 5) * 5;

  // Add a buffer of 10, rounded down to the next multiple of 5 so e.g. minX value of 90,5 will become 80
  minX = Math.floor((minX - 10) / 5) * 5;
  minY = Math.floor((minY - 10) / 5) * 5;

  return {
    responsive: true,
    scales: {
      x: {
        type: 'linear',
        title: {
          display: true,
          text: 'h°',
        },
        ticks: {
          max: maxX,
          min: minX,
        }
      },
      y: {
        title: {
          display: true,
          text: 'C*',
        },
        ticks: {
          max: maxY,
          min: minY
        }
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title(context) {
            return `45°:as${angleNames[context[0].dataIndex]}°`;
          },
          label(context) {
            // Undo the x (hue) manipulation for the tooltip label
            // See data manipulation above. Docu: https://trello.com/c/F6QPUFdr
            let xVal = context.parsed.x;
            if (xVal < 0 && (((xVal + 360) * 2 + 360) / 360) > 2) {
              xVal += 360;
            }

            return `${chromaConfig?.title}: ${context.parsed.y} / ${hueConfig?.title}: ${xVal}`;
          }
        }
      }
    },
  };
});
</script>

<template>
  <div class="ce-chromaHueChart">
    <div class="applicationType">
      {{ applicationTypeConfig.title }}
    </div>

    <div>
      <Line
        ref="chartRef"
        :data="data"
        :options="chartOptions" />
    </div>
  </div>
</template>
